import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * SortableList — wraps any array of items with keyboard + pointer + touch DnD.
 *
 * Each item MUST have a unique `_id` string field used as the DnD identifier.
 *
 * renderItem(item, index, dragHandleProps) → JSX
 *   Spread dragHandleProps onto the element that should act as the drag handle.
 */
export function SortableList({ items, onReorder, renderItem }) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i._id === active.id);
    const to   = items.findIndex((i) => i._id === over.id);
    if (from !== -1 && to !== -1) onReorder(arrayMove(items, from, to));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i._id)} strategy={verticalListSortingStrategy}>
        {items.map((item, idx) => (
          <SortableItem key={item._id} id={item._id}>
            {(handleProps) => renderItem(item, idx, handleProps)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

export function DragHandle(props) {
  return (
    <button
      {...props}
      type="button"
      className="p-1.5 text-white/20 hover:text-white/50 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none rounded transition-colors"
      title="Sıralamak için sürükle"
    >
      <GripVertical size={14} />
    </button>
  );
}
